'use client'

import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import type { ReactNode } from 'react'
import { useState } from 'react'

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { SELECT_CONTENT_CLASS } from '@/features/order/order-form/components/widget/collapsible/condition-editors/field-controls'
import {
  buildEeTypePreviewModel,
  countryToFlag,
  formatValue,
} from '@/lib/economic-calendar-display'
import { cn } from '@/lib/utils'
import { useEconomicCalendarHistory } from '@/service/economic-calendar.hook'

const EeTypeHistoryChart = dynamic(
  () => import('./ee-type-history-chart').then((m) => m.EeTypeHistoryChart),
  { ssr: false }
)

function PreviewSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      <div className="h-3 w-2/3 animate-pulse bg-white/10" />
      <div className="h-3 w-1/2 animate-pulse bg-white/10" />
      <div className="h-16 w-full animate-pulse bg-white/5" />
    </div>
  )
}

export function EeTypeChipPreview({ eeType, children }: { eeType: string; children: ReactNode }) {
  const t = useTranslations('orderForm.conditionEditor')
  const [open, setOpen] = useState(false)
  // Lazy: the request only fires once the card is actually open, and react-query
  // caches per-eeType so re-hovering the same chip doesn't refetch.
  const { data, isPending, isError } = useEconomicCalendarHistory({ eeType }, open)
  const model = data ? buildEeTypePreviewModel(data.events) : null

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={200}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      {open && isPending && (
        <HoverCardContent align="start" className={cn(SELECT_CONTENT_CLASS, 'w-72')}>
          <PreviewSkeleton />
        </HoverCardContent>
      )}
      {open && !isPending && !isError && model && (
        <HoverCardContent align="start" className={cn(SELECT_CONTENT_CLASS, 'w-72')}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-white/80">
              <span>{countryToFlag(model.headerEvent.country)}</span>
              <span className="truncate">{model.headerEvent.title}</span>
            </div>
            {model.nextRelease && (
              <p className="text-[11px] text-white/60">
                {t('eeTypePreview.nextRelease')}:{' '}
                <span className="font-medium text-white/80">
                  {formatValue(
                    model.nextRelease.consensus,
                    model.nextRelease.unit,
                    model.nextRelease.potency
                  )}
                </span>
              </p>
            )}
            {model.history.length >= 2 && <EeTypeHistoryChart history={model.history} />}
            {model.history.length === 1 && (
              <p className="text-[11px] text-white/60">
                {t('eeTypePreview.actual')}:{' '}
                <span className="font-medium text-white/80">
                  {formatValue(
                    model.history[0].actual,
                    model.history[0].unit,
                    model.history[0].potency
                  )}
                </span>{' '}
                / {t('eeTypePreview.consensus')}:{' '}
                <span className="font-medium text-white/80">
                  {formatValue(
                    model.history[0].consensus,
                    model.history[0].unit,
                    model.history[0].potency
                  )}
                </span>
              </p>
            )}
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  )
}
